import { REQUEST_METHODS } from '@/constants';
import { request } from '@umijs/max';
import type {
  IAreaTag,
  IKeywordTag,
  IMenu,
  ITaskDetail,
  ITemplateDetail,
  ITemplateFilterParams,
  ITemplateRecord,
  IUserInfo,
  IWorkspaceItem,
  TModule,
} from './type';

/**
 * 用户信息
 */
export const getUserInfo = () => {
  return request<IRes<IUserInfo>>('/supertext-uic/users/info');
};

/**
 * 菜单接口
 */
export const getMenus = () => {
  return request<IRes<IMenu[]>>('/supertext-uic/menus/navs');
};

// ! 任务相关
/**
 * 查询工作空间列表
 */
export const getWorkspaceList = (page: number, signal?: AbortSignal) => {
  return request<IRes<IList<IWorkspaceItem>>>('/supertext-java/workspace', {
    params: {
      page,
      size: 20,
    },
    signal,
  });
};

/**
 * 任务列表
 */
export const getTaskList = (keyword: string, signal?: AbortSignal) => {
  return request<IRes<IList<any>>>('/supertext-java/tasks', {
    params: {
      keyword,
    },
    signal,
  });
};

/**
 * 任务详情
 */
export const getTaskDetail = <T = unknown>(
  taskCode: string,
  signal?: AbortSignal,
) => {
  return request<IRes<ITaskDetail<T>>>(`/supertext-java/tasks/${taskCode}`, {
    signal,
  });
};

/** 任务失败重试 */
export const retryTask = (value: {
  task_code: string;
  template_code: string[];
}) => {
  return request<IRes<boolean>>(`/supertext-java/tasks/retry`, {
    method: 'PUT',
    data: value,
  });
};

/** 任务重命名 */
export const rename = (value: { task_code: string; task_name: string }) => {
  return request<IRes<boolean>>(
    `/supertext-java/tasks/${value.task_code}/name`,
    {
      method: 'PUT',
      data: { task_name: value.task_name },
    },
  );
};

// ! 模板相关
/**
 * 模板列表
 */
export const getTemplateList = (
  params?: Partial<ITemplateFilterParams>,
  signal?: AbortSignal,
) => {
  return request<IRes<IList<ITemplateRecord>>>('/supertext-java/templates', {
    params,
    signal,
  });
};

/**
 * 模板详情
 */
export const getTemplateDetail = <T = any>(
  templateCode: string,
  signal?: AbortSignal,
) => {
  return request<IRes<ITemplateDetail<T>>>(
    `/supertext-java/templates/${templateCode}`,
    {
      signal,
    },
  );
};
getTemplateDetail.queryKey = {
  method: 'GET',
  url: '/supertext-java/templates/templateCode',
};

/** 创建模版 */
export const createTemplate = (payload: {
  template_name: string;
  module: TModule;
  template_desc?: string;
  file_hash_list?: string[];
  is_favorite?: boolean;
  cover_image?: string;
  priority?: number;
  // todo 类型待完善
  template_config?:
    | {
        tags: Array<{
          name: string;
        }>;
      }
    | {
        combined_templates: Array<{
          template_code: string;
          template_name: string;
          module: TModule;
        }>;
      }
    | {
        tags: Array<{
          table_index: number;
          tag_code: string;
          name: string;
          selection_mode: 'KEYWORD' | 'AREA';
          keywords?: Array<{
            value: string;
            block_type: 'ROW' | 'COL' | 'CELL';
            start_offset: [number, number];
            end_offset: [number, number];
          }>;
          area?: {
            start_row: number;
            start_col: number;
            end_row: number;
            end_col: number;
          };
        }>;
      };
}) => {
  return request<IRes<ITemplateDetail<any>>>('/supertext-java/templates', {
    method: REQUEST_METHODS.post,
    data: payload,
  });
};

/** 更新模版 */
export const updateTemplate = (payload: {
  templateCode: string;
  data: {
    module: TModule;
    template_name?: string;
    status?: 0 | 1;
    template_config?:
      | {
          tags: Array<{
            name: string;
          }>;
        }
      | {
          combined_templates: Array<{
            template_code: string;
            template_name: string;
            module: TModule;
          }>;
        }
      | {
          tags: Array<IKeywordTag | IAreaTag>;
        };
  };
}) => {
  const { templateCode, data } = payload;

  return request<IRes<ITemplateDetail<any>>>(
    `/supertext-java/templates/${templateCode}`,
    {
      method: REQUEST_METHODS.put,
      data,
    },
  );
};

/**
 * 移除模板
 */
export const removeTemplate = (templateCode: string) => {
  return request<IRes<any>>(`/supertext-java/templates/${templateCode}`, {
    method: REQUEST_METHODS.delete,
  });
};

export const copyTemplate = (templateCode: string) => {
  return request<IRes<{ template_code: string; preview_task_code: string }>>(
    `/supertext-java/templates/${templateCode}/copy`,
    {
      method: REQUEST_METHODS.post,
    },
  );
};

/**
 * 导入模板
 */
export const importTemplate = (file: File) => {
  const data = new FormData();

  data.append('file', file);

  return request<IRes<any>>('/supertext-java/system/templates/imports', {
    method: REQUEST_METHODS.post,
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * 导出模板
 */
export const exportTemplate = (payload: {
  template_codes: string[];
  export_builtin_regulations?: boolean;
}) => {
  return request<Blob>('/supertext-java/system/templates/exports', {
    method: REQUEST_METHODS.post,
    responseType: 'blob',
    data: payload,
  });
};

/** 模板上架和下架 */
export const updateTemplateStatus = (params: {
  /**
   * 0 下架
   * 1 上架
   */
  status: 0 | 1;
  template_code: string;
}) => {
  return request<IRes<string>>('/supertext-java/templates/status', {
    data: params,
    method: 'PUT',
  });
};

/** 模板重命名 */
export const renameTemplate = (payload: {
  template_code: string;
  template_name: string;
}) => {
  return request<IRes<any>>('/supertext-java/templates/name', {
    method: REQUEST_METHODS.post,
    data: payload,
  });
};

/** 其他 */
export const verifyFileExist = (
  files: Array<{
    file_hash: string;
    filename: string;
  }>,
  signal?: AbortSignal,
) => {
  return request<IRes<{ file_hash: string[] }>>('/supertext-java/files/exist', {
    method: 'POST',
    data: {
      files,
    },
    signal,
  });
};

export type * from './type';
